import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { animated } from '@react-spring/web';

import BadgeBase from 'src/components/Badge';
import { hexToRgb } from 'src/utils/common';
import { hoverAndActiveCss } from 'src/utils/styled';

export const DesktopMoreButton = styled.button<{ isOpen?: boolean }>`
  all: unset;
  display: none;
  box-sizing: border-box;

  ${({ theme }) => theme.screenSize.desktop} {
    display: flex;
    align-items: center;
    justify-content: center;

    position: absolute;
    inset-inline-end: 24px;
    top: 17px;

    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.surface4};
    border: 1px solid ${({ theme }) => theme.color.surface5};
    cursor: pointer;

    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
    transition:
      opacity 0.2s ease-in-out,
      visibility 0.2s ease-in-out;
  }
`;

export const SwipeActionsWrapper = styled.div`
  display: flex;
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  height: 100%;

  ${({ theme }) => theme.screenSize.desktop} {
    display: none;
  }
`;

export const SWIPE_BUTTON_WIDTH = 76;

const SwipeActionButtonBase = styled.button<{ variant: 'danger' | 'warning' }>`
  all: unset;
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${SWIPE_BUTTON_WIDTH}px;
  height: 100%;
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  text-align: center;
  word-break: keep-all;
  padding: 4px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.contentBrightFixed};
  background-color: ${({ variant, theme }) =>
    variant === 'danger' ? theme.color.accentA1 : theme.color.accentB1};

  will-change: transform;
`;

export const AnimatedSwipeActionButton = styled(animated(SwipeActionButtonBase))``;

export const Time = styled.span`
  flex-shrink: 0;
  margin-inline-start: auto;
  font-size: 10px;
  color: ${({ theme }) => theme.color.content8};
`;

export const Badge = styled(BadgeBase)`
  margin-inline-start: auto;
`;

export const SwipeableContent = styled(animated.div)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 12px 16px;
  touch-action: pan-y;

  ${({ theme }) => theme.screenSize.desktop} {
    transform: none !important;
    touch-action: auto;
    padding: 12px 24px;
  }
  :hover {
    background: ${({ theme }) => theme.color.surface3};
  }
`;

export const Container = styled('li', {
  shouldForwardProp: isPropValid,
})<{ isSelected?: boolean; isMenuOpen?: boolean }>`
  position: relative;
  overflow: hidden;

  display: flex;
  align-items: center;

  color: #fff;

  ${({ isSelected, theme }) => isSelected && `background-color: ${theme.color.surface3};`}

  &:hover ${DesktopMoreButton} {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  &:hover ${Time}, &:hover ${Badge} {
    ${({ theme }) => theme.screenSize.desktop} {
      opacity: 0;
      visibility: hidden;
    }
  }

  ${({ isMenuOpen, theme }) =>
    isMenuOpen &&
    `
    ${theme.screenSize.desktop} {
      ${Time}, ${Badge} {
        opacity: 0;
        visibility: hidden;
      }
    }
  `}
`;

export const Action = styled.button`
  all: unset;
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: 16px;

  width: 100%;
  height: 100%;

  text-align: inherit;
`;

export const imageStyles = css`
  flex-shrink: 0;

  width: 56px;
  height: 56px;
  border-radius: 100%;
`;

export const AvatarContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

export const AvatarImage = styled.img`
  ${imageStyles}

  object-fit: cover;
`;

export const OnlineDot = styled.div`
  position: absolute;
  top: 1px;
  inset-inline-end: 1px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 3px;
  border-radius: 8px;

  width: 16px;
  height: 16px;

  border: 3px solid ${({ theme }) => theme.color.bg1};
  background-color: ${({ theme }) => theme.color.accentH1};
`;

export const Content = styled.div`
  flex: 1;
  min-width: 0%;
  max-height: 56px;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  height: 20px;
`;

export const UserName = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;

  color: ${({ theme }) => theme.color.content12};

  overflow: hidden;
  word-break: break-all;
  text-overflow: ellipsis;

  display: -webkit-box;
  display: box;
  -webkit-line-clamp: 2;

  line-clamp: 2;
  -webkit-box-orient: vertical;
  box-orient: vertical;
`;

export const MessageText = styled.span`
  flex: 1;

  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${({ theme }) => theme.color.content10};
`;

export const ModalBackdrop = styled.div<{ isDim: boolean }>`
  position: fixed;
  z-index: 98;
  flex: 1;
  height: 100%;
  top: 0px;
  left: 0px;
  width: var(--100vw);
  max-height: var(--100vh);
  display: flex;
  background: ${({ theme, isDim }) =>
    isDim ? `rgba(${hexToRgb(theme.color.surfaceDeepFixed)}, .7)` : 'transparent'};
`;

export const ModalContent = styled.div<{ buttonRect?: DOMRect }>`
  position: fixed;
  z-index: 99;

  ${({ buttonRect, theme }) => {
    if (!buttonRect || buttonRect.width <= 0) {
      return `
        left: 0;
        right: 0;
        bottom: 0;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
      `;
    }
    return `
      top: ${buttonRect.y + buttonRect.height}px;
      ${theme.isRtl ? `left: ${buttonRect.left}px;` : `left: ${buttonRect.right - 210}px;`}
    `;
  }}
`;

export const ModalMenuList = styled.ul`
  margin: 0;
  margin-top: 7px;
  padding: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.surface4};
  box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.25);
`;

export const ModalMenuItem = styled.li`
  list-style: none;
  width: 194px;
`;

export const ModalMenuButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 8px;
  background-color: transparent;
  color: ${({ theme }) => theme.color.content12};
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  border: none;

  ${({ theme }) =>
    hoverAndActiveCss(`
      background-color: ${theme.color.surface5};
      border-radius: 8px;
  `)}
`;

export const MenuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;

  svg {
    width: 22px;
    height: 22px;
  }
`;
